import { STORAGE_KEY, type TransferData } from '../../constants'
import { getUserOptions } from '../settings'
import { parsePublicationDate } from '../../utils/dom'

const REPLACE_BUTTON_CLASS = 'sg-enhancer-replace-button'
const REPLACE_BUTTON_STYLE = 'display:inline-flex; align-items:center; margin:4px 0 4px 8px; padding:3px 8px; border:1px solid #2e7d32; border-radius:999px; background:#f0f7f2; color:#2e7d32; font-size:11px; font-weight:700; line-height:1.4; cursor:pointer; vertical-align:middle;'

const STORYGRAPH_LANGUAGE_ALIASES: Record<string, string> = {
  'zh': 'chinese',
  'zh-cn': 'chinese',
  'zh-hans': 'chinese',
  'zh-hant': 'chinese',
  'zh-tw': 'chinese',
  'zho': 'chinese',
  'chi': 'chinese',
  '中文': 'chinese',
  '汉语': 'chinese',
  '漢語': 'chinese',
  '中国語': 'chinese',
  '簡體中文': 'chinese',
  '简体中文': 'chinese',
  '繁體中文': 'chinese',
  '繁体中文': 'chinese',
  'ja': 'japanese',
  'jp': 'japanese',
  'jpn': 'japanese',
  '日本語': 'japanese',
  '日文': 'japanese',
  '日語': 'japanese',
  '日本語版': 'japanese',
  'ko': 'korean',
  'kor': 'korean',
  '한국어': 'korean'
}

function normalizeStoryGraphLanguage (language: string): string {
  const trimmedLanguage = language.trim()
  return STORYGRAPH_LANGUAGE_ALIASES[trimmedLanguage] || STORYGRAPH_LANGUAGE_ALIASES[trimmedLanguage.toLowerCase()] || trimmedLanguage
}

function dispatchValueEvents (el: HTMLInputElement | HTMLTextAreaElement): void {
  el.dispatchEvent(new Event('input', { bubbles: true }))
  el.dispatchEvent(new Event('change', { bubbles: true }))
}

function applyValue (el: HTMLInputElement | HTMLTextAreaElement, value: string): void {
  el.value = value
  dispatchValueEvents(el)
  el.setAttribute('data-filled', 'true')
}

function getReplaceTargetKey (target: HTMLElement): string {
  return encodeURIComponent(target.id || target.getAttribute('name') || target.tagName)
}

function addReplaceButton (target: HTMLElement, value: string, onReplace: () => void): void {
  const replaceTargetKey = getReplaceTargetKey(target)
  if (target.parentElement?.querySelector(`.${REPLACE_BUTTON_CLASS}[data-replace-target="${replaceTargetKey}"]`)) return

  const button = document.createElement('button')
  button.type = 'button'
  button.className = REPLACE_BUTTON_CLASS
  button.dataset.replaceTarget = replaceTargetKey
  button.textContent = 'Replace'
  button.title = `Replace existing text with imported value: ${value}`
  button.style.cssText = REPLACE_BUTTON_STYLE
  button.addEventListener('click', () => {
    onReplace()
    button.remove()
  })

  target.insertAdjacentElement('afterend', button)
}

function setValueByName (name: string, value: string, skipExistingAutofill: boolean): boolean {
  const selector = `[name*="[${name}]"] , [name="${name}"] , [id*="_${name}"]`
  const el = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(selector)
  if (!el || !value || el.hasAttribute('data-filled')) return false

  const existingValue = el.value.trim()
  if (skipExistingAutofill && existingValue) {
    if (existingValue !== value) addReplaceButton(el, value, () => applyValue(el, value))
    return false
  }

  applyValue(el, value)
  return true
}

function applyTrixContent (trix: HTMLElement & { editor: any }, content: string): void {
  trix.editor.loadHTML('')
  const formattedHTML = content
    .split('\n')
    .map((line: string) => line.trim() ? `<div>${line}</div>` : '<div><br></div>')
    .join('')
  trix.editor.insertHTML(formattedHTML)
  trix.setAttribute('data-filled', 'true')
}

function fillTrixEditor (content: string, skipExistingAutofill: boolean): boolean {
  const trix = document.querySelector<any>('trix-editor')
  if (!trix || !trix.editor || trix.hasAttribute('data-filled') || !content) return false

  const existingText = trix.editor.getDocument?.().toString?.().trim?.() ?? ''
  if (skipExistingAutofill && existingText) {
    if (existingText !== content.trim()) addReplaceButton(trix, content, () => applyTrixContent(trix, content))
    return false
  }

  applyTrixContent(trix, content)
  return true
}

function showToast (title: string): void {
  if (document.getElementById('sync-toast')) return

  const toast = document.createElement('div')
  toast.id = 'sync-toast'
  toast.style.cssText = 'position:fixed; top:20px; right:20px; z-index:10001; padding:10px 20px; background:#2e7d32; color:white; border-radius:5px; font-weight:bold; box-shadow:0 2px 10px rgba(0,0,0,0.2);'

  const label = document.createElement('div')
  label.textContent = `✅ Data Ready: ${title}`
  toast.appendChild(label)

  document.body.appendChild(toast)

  window.setTimeout(() => {
    toast.remove()
  }, 4000)
}

function parseStoredData (): TransferData | null {
  const raw = GM_getValue(STORAGE_KEY)
  if (!raw) return null

  try {
    return JSON.parse(raw) as TransferData
  } catch {
    return null
  }
}

export function maybeAutofillStoryGraph (): void {
  const skipExistingAutofill = getUserOptions().skipExistingAutofill
  const path = window.location.pathname
  const isReviewPage = path.includes('/reviews/') || path.includes('/write_review')
  const isBookPage = path.includes('/add') ||
    path.includes('/update_missing_info') ||
    path.includes('/add_missing_info') ||
    path.includes('/books/new') ||
    (path.includes('/books') && path.includes('/edit'))
  if (!isReviewPage && !isBookPage) return

  const data = parseStoredData()
  if (!data) return

  let didFillField = false
  const setValue = (name: string, value: string): boolean => setValueByName(name, value, skipExistingAutofill)

  didFillField = setValue('title', data.title) || didFillField
  didFillField = setValue('author_names', data.author) || didFillField
  didFillField = setValue('language', normalizeStoryGraphLanguage(data.language)) || didFillField
  didFillField = setValue('publisher', data.publisher) || didFillField
  didFillField = setValue('isbn', data.isbn) || didFillField
  didFillField = setValue('number_of_pages', data.pages) || didFillField
  didFillField = setValue('format', data.format) || didFillField
  didFillField = setValue('rating', data.rating) || didFillField

  if (data.pubDate) {
    const parsed = parsePublicationDate(data.pubDate)
    if (parsed) didFillField = setValue('publication_date', parsed) || didFillField
  }

  didFillField = fillTrixEditor(isReviewPage ? data.review : data.desc, skipExistingAutofill) || didFillField

  if (!didFillField) return

  showToast(data.title)
  window.setTimeout(() => {
    GM_setValue(STORAGE_KEY, null)
  }, 4000)
}
